import { useNavigation } from '@react-navigation/native';
import { memo, ReactNode, useCallback, useEffect } from 'react';
import {
  StyleProp,
  StyleSheet,
  TouchableWithoutFeedback,
  useWindowDimensions,
  View,
  ViewStyle,
} from 'react-native';
import Animated, {
  Easing,
  interpolate,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

const kDefaultContentContainerWidthRatio = 0.3;

export const RightAside = memo(
  ({
    children,
    containerStyle,
    contentContainerStyle,
  }: {
    children?: ReactNode;
    containerStyle?: StyleProp<ViewStyle>;
    contentContainerStyle?: StyleProp<ViewStyle>;
  }) => {
    const navigation = useNavigation();
    const { width } = useWindowDimensions();

    const flattenedStyle = StyleSheet.flatten(contentContainerStyle);
    const contentContainerWidth =
      typeof flattenedStyle?.width == 'string'
        ? (width * parseInt(flattenedStyle?.width, 10)) / 100
        : flattenedStyle?.width != null &&
          typeof flattenedStyle.width === 'number'
        ? flattenedStyle?.width
        : width * kDefaultContentContainerWidthRatio;

    const opacity = useSharedValue(0);
    const animatedContentContainerStyle = useAnimatedStyle(() => ({
      transform: [
        {
          translateX: interpolate(
            opacity.value,
            [0, 1],
            [contentContainerWidth, 0],
          ),
        },
      ],
    }));

    const animatedContainerStyle = useAnimatedStyle(() => ({
      opacity: opacity.value,
    }));

    const dismiss = useCallback(() => {
      navigation.goBack();
    }, [navigation]);

    const onDismiss = useCallback(() => {
      opacity.value = withTiming(
        0,
        { duration: 150, easing: Easing.inOut(Easing.quad) },
        () => {
          runOnJS(dismiss)();
        },
      );
    }, [dismiss, opacity]);

    useEffect(() => {
      opacity.value = withTiming(1, {
        duration: 250,
        easing: Easing.inOut(Easing.quad),
      });
    }, [opacity]);

    return (
      <Animated.View
        css={`
          flex: 1;
          background-color: rgba(0, 0, 0, 0.5);
        `}
        style={[containerStyle, animatedContainerStyle]}
      >
        <TouchableWithoutFeedback onPress={onDismiss}>
          <View style={StyleSheet.absoluteFill} />
        </TouchableWithoutFeedback>
        <Animated.View
          css={`
            width: ${width * kDefaultContentContainerWidthRatio}px;
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
          `}
          style={[
            {
              transform: [{ translateX: contentContainerWidth }],
            },
            contentContainerStyle,
            animatedContentContainerStyle,
          ]}
        >
          {children}
        </Animated.View>
      </Animated.View>
    );
  },
);
